import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IressStack, useToaster } from '@iress-oss/ids-components';
import {
  saveActionsRequestStrict,
  type SuggestedAction,
} from '@meeting-assistant/schemas';
import { MeetingWorkspaceLayout } from '@/components/MeetingWorkspaceLayout';
import { TranscriptSlideout } from '@/components/ResponsiveTranscriptPanel';
import { AnalysisHeader } from '@/components/actions/AnalysisHeader';
import {
  ActionsSection,
  type ActionsSectionHandle,
} from '@/components/actions/ActionsSection';
import { ActionsFooter } from '@/components/actions/ActionsFooter';
import { useMeeting } from '@/lib/hooks/useMeeting';
import { saveActions, postFinalise } from '@/lib/api';
import { ApiError } from '@/lib/errors/ApiError';
import { ProgressStepper } from '@/components/ProgressStepper';
import { ProgressStep } from '@/lib/progress/steps';
import { meetingsQueryOptions } from '@/lib/query/meetingsQuery';
import { AiDisclaimer } from '@/components/AiDisclaimer';
export const Route = createFileRoute('/$id/actions')({
  component: ActionsPage,
});

/** Which step of the save flow failed, so the toast can name the right thing. */
type SaveStage = 'fileNote' | 'actions';

/** Wraps an underlying error with the save-flow stage that produced it. */
class SaveError extends Error {
  constructor(
    readonly stage: SaveStage,
    readonly cause: unknown,
  ) {
    super(`Save failed at stage: ${stage}`);
    this.name = 'SaveError';
  }
}

function ActionsPage() {
  const { data: meeting, isLoading } = useMeeting();
  const navigate = useNavigate();
  const toaster = useToaster();
  const queryClient = useQueryClient();
  const { id } = Route.useParams();
  const [selectedActions, setSelectedActions] = useState<SuggestedAction[]>([]);
  const actionsSectionRef = useRef<ActionsSectionHandle>(null);
  const clientName = meeting?.client?.main?.name ?? '';
  const clientFirstName = clientName.split(' ')[0];
  const clientId = meeting?.client?.main?.id;
  const transcript = meeting?.transcript?.content;
  const meetingDate = meeting?.datetime
    ? new Date(meeting.datetime).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (clientId === undefined) {
        throw new SaveError(
          'actions',
          new Error('Cannot save actions without a client ID'),
        );
      }

      const saveActionsBody = {
        meetingId: id,
        clientId,
        actions: selectedActions.map((action) => ({
          id: action.id,
          subject: action.title,
          description: action.description,
          priority: action.priority,
          assigneeId: action.assigneeUser
            ? Number(action.assigneeUser.value)
            : null,
          dueDate: action.dueDate,
        })),
      };

      // Validate the action payload before finalising the file note.
      saveActionsRequestStrict.parse(saveActionsBody);

      // Save the file note to XPLAN first, then the selected actions.
      //
      // A 409 means a file note already exists for this meeting — that's not a
      // failure for our purposes, so we continue on to save the actions and
      // note that the file note already existed. Any other file-note error
      // stops the flow before touching actions, so the user can retry from a
      // known state (and stays on the page).
      let fileNoteAlreadyExisted = false;
      try {
        await postFinalise(id);
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          fileNoteAlreadyExisted = true;
        } else {
          throw new SaveError('fileNote', error);
        }
      }

      try {
        const actions = await saveActions(saveActionsBody);
        return { fileNoteAlreadyExisted, actions };
      } catch (error) {
        throw new SaveError('actions', error);
      }
    },
    onSuccess: ({ fileNoteAlreadyExisted, actions }) => {
      // The toast type is driven purely by the actions outcome — the file note
      // is reported either way (saved vs already existed) but never fails the
      // toast on its own.
      const fileNotePart = fileNoteAlreadyExisted
        ? 'File note already exists'
        : 'File note saved';

      if (actions.actionsFailed > 0) {
        // Partial failure counts as a failure: stay on the page so the adviser
        // can retry the actions that didn't save. Drop only the actions that
        // *did* save (the backend tells us which) so they can't be retried, and
        // do NOT finalise or refresh the meetings list — the meeting is not
        // finalised server-side when any action fails.
        actionsSectionRef.current?.removeActions(actions.savedActionIds);
        const totalActions = actions.actionsSaved + actions.actionsFailed;
        toaster.error(
          `${fileNotePart}, ${actions.actionsSaved} out of ${totalActions} action(s) saved to Xplan.`,
        );
        return;
      }

      // Clean success: every selected action saved and the meeting is finalised
      // server-side. Drop the saved actions, refresh the home page list (the
      // cached list has staleTime: Infinity so it must be invalidated to show
      // the new finalised status), and return home.
      actionsSectionRef.current?.removeActions(actions.savedActionIds);
      toaster.success(
        `${fileNotePart}, ${actions.actionsSaved} action(s) saved to Xplan.`,
      );
      void queryClient.invalidateQueries({
        queryKey: meetingsQueryOptions.queryKey,
      });
      void navigate({ to: '/' });
    },
    onError: (error) => {
      const stage = error instanceof SaveError ? error.stage : 'actions';
      toaster.error(
        stage === 'fileNote'
          ? 'Failed to save file note. Please try again.'
          : 'Failed to save actions. Please try again.',
      );
    },
    // Failures are surfaced as a toast, not by unmounting the page.
    throwOnError: false,
  });

  return (
    <MeetingWorkspaceLayout
      clientName={clientName}
      meetingDate={meetingDate}
      transcript={transcript}
      isLoading={isLoading}
      transcriptSlideout={<TranscriptSlideout entries={transcript} />}
    >
      <IressStack
        style={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        gap="spacing.2"
      >
        <ProgressStepper step={ProgressStep.UpdateXplan} />
        <IressStack
          p="none"
          stretch
          flex="1"
          gap="spacing.4"
          style={{ minHeight: 0, overflow: 'hidden' }}
        >
          <AnalysisHeader />
          <ActionsSection
            ref={actionsSectionRef}
            clientFirstName={clientFirstName}
            meetingDate={meetingDate}
            id={meeting?.id ?? ''}
            onSelectionChange={setSelectedActions}
          />
          <AiDisclaimer />
        </IressStack>
        <ActionsFooter
          selectedCount={selectedActions.length}
          onReturnToSummary={() =>
            void navigate({ to: '/$id/summarise', params: { id } })
          }
          onSaveAndNext={() => saveMutation.mutate()}
          saveDisabled={saveMutation.isPending || clientId === undefined}
          isSaving={saveMutation.isPending}
        />
      </IressStack>
    </MeetingWorkspaceLayout>
  );
}
